import type React from "react";
import { Box, Typography } from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useTranslation } from "react-i18next";
import DetailSectionCard from "../common/DetailSectionCard";
import OrderStatusChip from "../common/OrderStatusChip";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import { OrderStatus } from "../../types/order";
import { SuratJalanStatus } from "../../types/suratJalan";
import { UangJalanReportStatus } from "../../types/uangJalanReport";

type TranslationKeys = keyof typeof translations.en;

const ORDER_STATUS_ORDER: OrderStatus[] = [
  OrderStatus.OPEN,
  OrderStatus.READY_FOR_REVIEW,
  OrderStatus.IN_REVIEW,
  OrderStatus.REVISION_REQUIRED,
  OrderStatus.VERIFIED,
  OrderStatus.APPROVED,
  OrderStatus.APPROVED_WITH_OVERRIDE,
  OrderStatus.COMPLETED,
];

const SURAT_JALAN_STATUS_ORDER: SuratJalanStatus[] = [
  SuratJalanStatus.IN_REVIEW,
  SuratJalanStatus.INVOICING_REVIEW,
  SuratJalanStatus.REVISION_REQUIRED,
  SuratJalanStatus.APPROVED,
];

const REPORT_STATUS_ORDER: UangJalanReportStatus[] = [
  UangJalanReportStatus.OPEN,
  UangJalanReportStatus.IN_REVIEW,
  UangJalanReportStatus.REVISION_REQUIRED,
  UangJalanReportStatus.VERIFIED,
  UangJalanReportStatus.APPROVED,
  UangJalanReportStatus.APPROVED_WITH_OVERRIDE,
];

interface DashboardStatusBreakdownProps {
  ordersByStatus: Record<OrderStatus, number>;
  reportsByStatus: Record<UangJalanReportStatus, number>;
  suratJalanByStatus: Record<SuratJalanStatus, number>;
}

const StatusRows: React.FC<{
  statuses: Array<{ status: string; count: number }>;
}> = ({ statuses }) => {
  const visible = statuses.filter((item) => item.count > 0);

  if (visible.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2 }}>
      {visible.map((item) => (
        <Box
          key={item.status}
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
        >
          <OrderStatusChip status={item.status} />
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
            {item.count}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const DashboardStatusBreakdown: React.FC<DashboardStatusBreakdownProps> = ({
  ordersByStatus,
  reportsByStatus,
  suratJalanByStatus,
}) => {
  const { t } = useTranslation();

  const orderRows = ORDER_STATUS_ORDER.map((status) => ({
    status,
    count: ordersByStatus[status] ?? 0,
  }));
  const reportRows = REPORT_STATUS_ORDER.map((status) => ({
    status,
    count: reportsByStatus[status] ?? 0,
  }));
  const suratJalanRows = SURAT_JALAN_STATUS_ORDER.map((status) => ({
    status,
    count: suratJalanByStatus[status] ?? 0,
  }));

  const allZero =
    orderRows.every((row) => row.count === 0) &&
    reportRows.every((row) => row.count === 0) &&
    suratJalanRows.every((row) => row.count === 0);

  return (
    <DetailSectionCard
      title={t("dashboard.workflow" as TranslationKeys)}
      icon={<AccountTreeOutlinedIcon fontSize="small" />}
      iconColor="#7C3AED"
      sx={{ mb: 2 }}
      data-testid={TestId.DASHBOARD.WORKFLOW}
    >
      {allZero ? (
        <Typography color="text.secondary">
          {t("dashboard.noActivityInRange" as TranslationKeys)}
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("Orders" as TranslationKeys)}
          </Typography>
          <StatusRows statuses={orderRows} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("Uang Jalan Report" as TranslationKeys)}
          </Typography>
          <StatusRows statuses={reportRows} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("Surat Jalan" as TranslationKeys)}
          </Typography>
          <StatusRows statuses={suratJalanRows} />
        </>
      )}
    </DetailSectionCard>
  );
};

export default DashboardStatusBreakdown;
